/**
 * Settings — account-level settings for the signed-in user.
 *
 * Scaffold for now: surfaces account info and the billing portal link for
 * clients. Future home for notification preferences, timezone, webhook
 * config, and (for owners) agency-wide defaults.
 */
import { useEffect, useState } from 'react';

import { AppShell } from '../components/AppShell';
import { useApi, type MeResponse } from '../lib/api';

type AgencySetupLinkResponse = {
  setup_token: string;
  setup_url: string;
  expires_in_seconds: number;
  mode: 'agency';
};

export function Settings() {
  const { request } = useApi();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  // Agency-mode extension connect (Phase 4 of AGENCY_MODE_SPEC).
  // The link is one-time and expires in 15 min — we surface a countdown
  // and let the user generate another with the same button.
  const [agencyLink, setAgencyLink] = useState<AgencySetupLinkResponse | null>(null);
  const [agencyLinkError, setAgencyLinkError] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  const [linkExpiresAt, setLinkExpiresAt] = useState<number | null>(null);
  const [, forceTick] = useState(0);

  useEffect(() => {
    request<MeResponse>('/api/me')
      .then(setMe)
      .catch(() => setMe(null));
  }, [request]);

  // 1s ticker so the "expires in N:MM" countdown updates without us
  // having to manage individual setIntervals per render.
  useEffect(() => {
    if (!linkExpiresAt) return;
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [linkExpiresAt]);

  const userRole = me && !me.user_not_linked ? me.user.role : undefined;
  // Anyone in an agency (owner, va_admin, plain va) can connect their own
  // browser as an agency-mode extension. Plain `client` users have no
  // agency identity to bind to — they use the per-client setup link from
  // their welcome email instead.
  const canConnectAgency =
    !!userRole && userRole !== 'client';

  async function openBillingPortal() {
    setOpeningPortal(true);
    setPortalError(null);
    try {
      const res = await request<{ url: string }>('/api/me/billing-portal', {
        method: 'POST',
      });
      window.location.href = res.url;
    } catch (e) {
      setPortalError((e as Error).message);
    } finally {
      setOpeningPortal(false);
    }
  }

  async function generateAgencyLink() {
    setGeneratingLink(true);
    setAgencyLinkError(null);
    setCopiedAt(null);
    try {
      const res = await request<AgencySetupLinkResponse>(
        '/api/me/extensions/agency-setup-link',
        { method: 'POST' },
      );
      setAgencyLink(res);
      setLinkExpiresAt(Date.now() + res.expires_in_seconds * 1000);
    } catch (e) {
      setAgencyLinkError((e as Error).message);
    } finally {
      setGeneratingLink(false);
    }
  }

  async function copyLink() {
    if (!agencyLink) return;
    try {
      await navigator.clipboard.writeText(agencyLink.setup_url);
      setCopiedAt(Date.now());
    } catch {
      setAgencyLinkError(
        'Clipboard blocked — select the link manually and copy.',
      );
    }
  }

  function formatCountdown(): string {
    if (!linkExpiresAt) return '';
    const ms = linkExpiresAt - Date.now();
    if (ms <= 0) return 'expired';
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  const linkExpired = !!linkExpiresAt && linkExpiresAt - Date.now() <= 0;

  return (
    <AppShell {...(userRole ? { userRole } : {})}>
      <main className="page">
        <div className="page-hero">
          <div className="page-hero-icon" aria-hidden="true">⚙️</div>
          <div className="page-hero-text">
            <h1 className="page-hero-title">Settings</h1>
            <p className="page-hero-sub">
              Your account, billing, and preferences.
            </p>
          </div>
        </div>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Account</h2>
          {me && !me.user_not_linked ? (
            <div style={{ lineHeight: 2 }}>
              <div>
                <span className="muted">Email:</span>{' '}
                <strong>{me.user.email}</strong>
              </div>
              <div>
                <span className="muted">Role:</span>{' '}
                <strong style={{ textTransform: 'capitalize' }}>
                  {me.user.role}
                </strong>
              </div>
              {me.user.timezone && (
                <div>
                  <span className="muted">Timezone:</span>{' '}
                  <strong>{me.user.timezone}</strong>
                </div>
              )}
            </div>
          ) : (
            <p className="muted">Loading…</p>
          )}
        </section>

        {me && !me.user_not_linked && me.user.role === 'client' && (
          <section className="card">
            <h2 style={{ marginTop: 0 }}>Billing</h2>
            <p className="muted" style={{ marginTop: 4 }}>
              Manage your subscription, payment method, and invoices through
              the Stripe customer portal.
            </p>
            <div className="actions" style={{ justifyContent: 'flex-start' }}>
              <button
                className="btn btn-secondary"
                onClick={openBillingPortal}
                disabled={openingPortal}
              >
                {openingPortal ? 'Opening…' : 'Manage Subscription'}
              </button>
            </div>
            {portalError && (
              <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 0 }}>
                {portalError}
              </p>
            )}
          </section>
        )}

        {canConnectAgency && (
          <section className="card">
            <h2 style={{ marginTop: 0 }}>Connect agency extension</h2>
            <p className="muted" style={{ marginTop: 4 }}>
              Run one Chrome extension on your own machine and switch
              between any client you manage. Use this if you're a VA or
              owner capturing leads for multiple clients from the same
              browser profile. (Each client's own per-client install
              keeps working unchanged.)
            </p>

            <div
              className="actions"
              style={{ justifyContent: 'flex-start', marginTop: 12 }}
            >
              <button
                className="btn btn-secondary"
                onClick={generateAgencyLink}
                disabled={generatingLink}
              >
                {generatingLink
                  ? 'Generating…'
                  : agencyLink
                    ? 'Generate a new link'
                    : 'Generate setup link'}
              </button>
            </div>

            {agencyLinkError && (
              <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 12 }}>
                {agencyLinkError}
              </p>
            )}

            {agencyLink && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: '#f5f5f7',
                  borderRadius: 8,
                  border: '1px solid #e5e5e7',
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#6e6e73',
                    marginBottom: 6,
                  }}
                >
                  One-time setup link{' '}
                  {linkExpiresAt && (
                    <span
                      style={{
                        color: linkExpired ? '#b91c1c' : '#047857',
                        fontWeight: 600,
                      }}
                    >
                      · {linkExpired ? 'expired' : `expires in ${formatCountdown()}`}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: 12,
                    wordBreak: 'break-all',
                    background: '#fff',
                    padding: 10,
                    borderRadius: 6,
                    border: '1px solid #e5e5e7',
                    color: '#1d1d1f',
                    userSelect: 'all',
                  }}
                >
                  {agencyLink.setup_url}
                </div>
                <div
                  className="actions"
                  style={{ justifyContent: 'flex-start', marginTop: 10 }}
                >
                  <button
                    className="btn btn-primary"
                    onClick={copyLink}
                    disabled={linkExpired}
                  >
                    {copiedAt && Date.now() - copiedAt < 2000
                      ? 'Copied ✓'
                      : 'Copy link'}
                  </button>
                </div>
                <ol
                  style={{
                    marginTop: 14,
                    marginBottom: 0,
                    paddingLeft: 20,
                    fontSize: 13,
                    color: '#3a3a3c',
                    lineHeight: 1.6,
                  }}
                >
                  <li>Install the GroupPulse extension if you haven't yet.</li>
                  <li>
                    Open the extension's <strong>Settings</strong> page (right-click
                    the puzzle icon → GroupPulse → Options).
                  </li>
                  <li>
                    Paste this link into <strong>Section 1B — Agency mode</strong>{' '}
                    and click <strong>Connect agency</strong>.
                  </li>
                  <li>
                    Open the extension popup and pick which client to start
                    capturing for.
                  </li>
                </ol>
                <p
                  style={{
                    marginTop: 12,
                    marginBottom: 0,
                    fontSize: 12,
                    color: '#6e6e73',
                  }}
                >
                  This link is one-time and tied to your account. Don't share
                  it — anyone who pastes it into a GroupPulse extension
                  before it expires gains capture access to every client
                  you can see.
                </p>
              </div>
            )}
          </section>
        )}

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Coming Up Next</h2>
          <p className="muted" style={{ marginBottom: 0 }}>
            Notification preferences, timezone editing, and webhook
            configuration will live here.
          </p>
        </section>
      </main>
    </AppShell>
  );
}
